import { FC, ReactNode } from 'react';

import { Drawer, DrawerHead, Section, SessionVerdict, StatusChip } from 'views/design-system';

import { Empty, KVRows } from '../bits';
import type {
	IFincenConnection,
	IFincenSubmission,
	IFincenSubmissionStatus,
	ISarDraft,
	ISarFilerProfileResp,
} from '../data/types';
import { useCaseSar } from '../data/use-tm';

import './case-drawer.scss';

/**
 * THE CASE DRAWER — where this case's SAR stands.
 *
 * Four questions, in the order they block a filing:
 *
 *   1. Is the draft ready? Its status, whether the controls recommend a SAR, and
 *      every GAP spelled out. A gap is the reason a SAR cannot be filed yet, so
 *      each one is shown with its field and its why — never collapsed to a count.
 *   2. Who is filing? The business's filer profile. Without transmitter and
 *      filing-institution details nothing can be transmitted, so an empty profile
 *      is shown as a gap, not as a blank row.
 *   3. What was sent? Every FinCEN submission for the case, newest first, and
 *      EVERY status carries its environment in the same badge. The sandbox (`test`)
 *      is FinCEN's User Test system: a batch it accepts is NOT a filing, and a
 *      green "ACCEPTED" on its own would say that it was. Sandbox acceptances are
 *      therefore never drawn green.
 *   4. Could anything be sent? The SDTM connection state. An empty history means
 *      "nothing submitted" when SDTM is configured and "cannot submit" when it is
 *      not — two different situations that an empty list alone cannot tell apart.
 *
 * Read-only. Nothing here submits, persists a draft, or changes the case: the
 * draft is `POST /sar-drafts` WITHOUT `persist`, which is a pure build.
 */

const DRAFT_STATUS: Record<ISarDraft['status'], [SessionVerdict | 'wait' | 'cfg' | 'pur', string]> = {
	ready: ['ok', 'READY'],
	needs_information: ['rev', 'NEEDS INFORMATION'],
	no_activity: ['ns', 'NO ACTIVITY'],
	shadow_only: ['ns', 'SHADOW RULES ONLY'],
};

const CASE_STATUS: Record<string, [SessionVerdict | 'wait' | 'cfg' | 'pur', string]> = {
	OPEN: ['strt', 'OPEN'],
	INVESTIGATING: ['pur', 'INVESTIGATING'],
	PENDING_SAR: ['wait', 'PENDING SAR'],
	/* The panel's "File SAR" only stamps the case — it transmits nothing — so the
	   header does not say "filed" in green on the strength of that stamp alone. */
	SAR_FILED: ['rev', 'MARKED SAR FILED'],
	CLOSED: ['ns', 'CLOSED'],
};

const SUB_LABEL: Record<IFincenSubmissionStatus, string> = {
	SUBMITTED: 'SUBMITTED',
	MESSAGES_RECEIVED: 'MESSAGES RECEIVED',
	ACCEPTED: 'ACCEPTED',
	ACCEPTED_WITH_WARNINGS: 'ACCEPTED WITH WARNINGS',
	REJECTED: 'REJECTED',
	NEEDS_REVIEW: 'NEEDS REVIEW',
};

const subVerdict = (s: IFincenSubmission): SessionVerdict | 'wait' | 'cfg' | 'pur' => {
	const prod = s.environment === 'production';
	switch (s.status) {
		case 'ACCEPTED':
			/* Green ONLY for production. The test system accepting a batch proves
			   the pipe works; it files nothing. */
			return prod ? 'ok' : 'ns';
		case 'ACCEPTED_WITH_WARNINGS':
			return prod ? 'rev' : 'ns';
		case 'REJECTED':
			return 'rej';
		case 'NEEDS_REVIEW':
			return 'rev';
		case 'MESSAGES_RECEIVED':
			return 'wait';
		default:
			return 'strt';
	}
};

/* The environment and the status are ONE unit: a nowrap group, so no layout can
   ever put a status on a line without the system it came from. */
const SubmissionBadge: FC<{ s: IFincenSubmission }> = ({ s }) => (
	<span className="csar-badge" data-qa="csar-submission-badge" data-env={s.environment}>
		{s.environment === 'production' ? (
			<StatusChip verdict="pur">PRODUCTION</StatusChip>
		) : (
			<StatusChip verdict="cfg">SANDBOX · NOT A FILING</StatusChip>
		)}
		<StatusChip verdict={subVerdict(s)}>
			{s.environment === 'production' ? '' : 'TEST '}
			{SUB_LABEL[s.status] ?? s.status}
		</StatusChip>
	</span>
);

const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : null);

const has = (v: unknown) =>
	v !== null && v !== undefined && v !== '' && !(typeof v === 'object' && !Object.keys(v as object).length);

/** Is any of the profile actually filled in? `{transmitter: {}}` is as empty as `{}`. */
const profileIsEmpty = (profile: Record<string, any> | undefined) =>
	!profile ||
	!Object.values(profile).some(part =>
		part && typeof part === 'object' ? Object.values(part).some(has) : has(part)
	);

const GapList: FC<{ gaps: { field: string; why: string }[]; title: ReactNode; qa: string }> = ({
	gaps,
	title,
	qa,
}) => (
	<div className="csar-gaps" data-qa={qa}>
		<div className="csar-gh">{title}</div>
		<ul>
			{gaps.map(g => (
				<li key={g.field}>
					<b>{g.field}</b>
					<span>{g.why}</span>
				</li>
			))}
		</ul>
	</div>
);

const ReadinessSection: FC<{ draft: ISarDraft | null; draftEmpty: boolean; loading: boolean }> = ({
	draft,
	/* TRUE WHEN THE CASE ITSELF HAS NOTHING TO BUILD FROM — derived from the case,
	   not from the upstream message. The network layer now swallows this
	   endpoint's 4xx (it is a normal state, not an error worth a toast), so the
	   message is gone by the time it reaches here; and matching on an upstream
	   sentence would break the first time somebody reworded it. */
	draftEmpty,
	loading,
}) => {
	if (!draft) {
		return (
			<Section title="SAR readiness">
				<Empty>
					{loading
						? 'Building the draft from records…'
						: draftEmpty
							? /* A REAL ANSWER, NOT A FAILURE. A case with no transactions and no
							     alerts has nothing to build a SAR from, and the builder says so.
							     Calling that "could not build" sends someone looking for an
							     outage that is not there. */
								'No activity on this case yet — there are no transactions or alerts to build a SAR from. Pin evidence or link an alert first.'
							: 'The SAR draft could not be read. This is unknown, not empty: do not read it as "no SAR needed".'}
				</Empty>
			</Section>
		);
	}
	const [verdict, label] = DRAFT_STATUS[draft.status] ?? ['ns', draft.status];
	return (
		<Section title="SAR readiness" right={<StatusChip verdict={verdict}>{label}</StatusChip>}>
			<div className="csar-body" data-qa="csar-readiness" data-status={draft.status}>
				<table className="kv wide">
					<tbody>
						<KVRows
							rows={[
								[
									'recommended',
									draft.recommended ? 'yes' : 'no',
									draft.recommended ? 'a' : undefined,
								],
								['transactions', String(draft.transactions.length)],
								[
									'controls',
									`${draft.rules.length} (${draft.rules.filter(r => r.mode !== 'TEST').length} active)`,
								],
								['fields filled', String(draft.fields.length)],
								['gaps', String(draft.gaps.length), draft.gaps.length ? 'rd' : 'g'],
							]}
						/>
					</tbody>
				</table>
				<div className="csar-note">{draft.recommendation}</div>

				{draft.gaps.length > 0 ? (
					<GapList
						qa="csar-gaps"
						gaps={draft.gaps}
						title={
							<>
								Cannot be filed yet — {draft.gaps.length} field{draft.gaps.length === 1 ? '' : 's'}{' '}
								missing
							</>
						}
					/>
				) : (
					draft.status === 'ready' && (
						<div className="csar-ok">
							Nothing missing. The draft is complete — transmitting it is a separate step.
						</div>
					)
				)}

				{draft.validation.length > 0 && (
					<GapList
						qa="csar-validation"
						gaps={draft.validation.map(v => ({ field: 'Schema', why: v }))}
						title="Schema problems"
					/>
				)}

				{draft.advisories.length > 0 && (
					<div className="csar-adv" data-qa="csar-advisories">
						<div className="csar-gh">Advisories</div>
						<ul>
							{draft.advisories.map(a => (
								<li key={a.field}>
									<b>{a.field}</b> — {a.why}
								</li>
							))}
						</ul>
					</div>
				)}
			</div>
		</Section>
	);
};

const FilerSection: FC<{ filer: ISarFilerProfileResp | null; draft: ISarDraft | null; loading: boolean }> = ({
	filer,
	draft,
	loading,
}) => {
	if (!filer) {
		return (
			<Section title="Filer profile">
				<Empty>
					{loading ? 'Reading the filer profile…' : 'Could not read the SAR filer profile for this business.'}
				</Empty>
			</Section>
		);
	}
	const p = filer.profile ?? {};
	const empty = profileIsEmpty(p);
	/* The draft already reports which filer fields are missing; point at those
	   rather than re-deriving the rule here, where it would drift from the builder. */
	const filerGaps = (draft?.gaps ?? []).filter(g => /filer profile/i.test(g.why));
	const part = (name: string | undefined) => name ?? <span className="rd">missing</span>;
	return (
		<Section
			title="Filer profile"
			right={
				empty ? (
					<StatusChip verdict="rej">NOT SET</StatusChip>
				) : filerGaps.length ? (
					<StatusChip verdict="rev">INCOMPLETE</StatusChip>
				) : (
					<StatusChip verdict="ok">COMPLETE</StatusChip>
				)
			}
		>
			<div className="csar-body" data-qa="csar-filer" data-empty={empty}>
				{empty ? (
					<GapList
						qa="csar-filer-empty"
						title="Cannot be transmitted"
						gaps={[
							{
								field: 'No SAR filer profile for this business',
								why: 'a SAR carries the transmitter, the filing institution and a contact office, and none of them has been entered. Nothing can be sent to FinCEN until they are.',
							},
						]}
					/>
				) : (
					<>
						<table className="kv wide">
							<tbody>
								<KVRows
									rows={[
										['transmitter', part(p.transmitter?.name)],
										['filing institution', part(p.filingInstitution?.name)],
										['contact office', part(p.contactOffice?.name)],
										['updated', when(filer.updatedAt)],
									]}
								/>
							</tbody>
						</table>
						{filerGaps.length > 0 && (
							<div className="csar-note">
								{filerGaps.length} of the gaps above {filerGaps.length === 1 ? 'is' : 'are'} filer-profile
								fields: {filerGaps.map(g => g.field).join(', ')}.
							</div>
						)}
					</>
				)}
			</div>
		</Section>
	);
};

const ENV_NAME: Record<string, string> = {
	test: 'BSA E-Filing sandbox (test)',
	production: 'BSA E-Filing production',
};

const SubmissionsSection: FC<{
	fincen: { connection: IFincenConnection; submissions: IFincenSubmission[] } | null;
	loading: boolean;
}> = ({ fincen, loading }) => {
	if (!fincen) {
		return (
			<Section title="FinCEN submissions">
				<Empty>
					{loading
						? 'Reading submissions…'
						: 'Could not reach fincen-submissions on transaction monitoring — the history is unknown, not empty.'}
				</Empty>
			</Section>
		);
	}
	const { connection: c, submissions } = fincen;
	return (
		<Section title="FinCEN submissions" right={`${submissions.length} for this case`}>
			<div className="csar-body" data-qa="csar-submissions" data-configured={c.configured}>
				<table className="kv wide">
					<tbody>
						<KVRows
							rows={[
								[
									'SDTM',
									c.configured ? 'configured' : 'not configured',
									c.configured ? 'g' : 'rd',
								],
								['system', c.environment ? ENV_NAME[c.environment] ?? c.environment : null],
								['host', c.host ? <span className="mono">{c.host}</span> : null],
								[
									'host key',
									c.hostKeyPinned === undefined ? null : c.hostKeyPinned ? 'pinned' : 'NOT pinned',
									c.hostKeyPinned === false ? 'rd' : undefined,
								],
							]}
						/>
					</tbody>
				</table>

				{c.configured && c.environment === 'test' && (
					<div className="csar-note">
						This service submits to FinCEN&apos;s <b>test</b> system only. Anything sent from here is a
						test of the connection, not a filing.
					</div>
				)}

				{submissions.length === 0 &&
					(c.configured ? (
						<Empty>
							Nothing submitted for this case yet. SDTM is configured, so an empty list here means no
							batch has been sent — not that one could not be.
						</Empty>
					) : (
						<GapList
							qa="csar-sdtm-unconfigured"
							title="Cannot be transmitted from this environment"
							gaps={[
								{
									field: 'FinCEN SDTM is not configured',
									why: 'transaction monitoring has no SDTM credentials, so nothing has been — or can be — submitted from here. An empty history is not the same as nothing owed.',
								},
							]}
						/>
					))}

				{submissions.map(s => {
					const last = s.responses?.[s.responses.length - 1];
					const errs = (s.responses ?? []).reduce((n, r) => n + (r.fatalErrors?.length ?? 0), 0);
					const warns = (s.responses ?? []).reduce((n, r) => n + (r.warnings?.length ?? 0), 0);
					return (
						<div className={`csar-sub ${s.environment}`} key={s._id} data-qa="csar-submission">
							<div className="csar-subh">
								<SubmissionBadge s={s} />
								<span className="csar-at">{when(s.submittedAt)}</span>
							</div>
							<table className="kv wide">
								<tbody>
									<KVRows
										rows={[
											[
												'BSA ID',
												s.bsaId ? (
													<span className="mono">
														{s.bsaId}
														{s.environment === 'test' ? ' (test system)' : ''}
													</span>
												) : null,
											],
											['batch', <span className="mono" key="rn">{s.remoteName}</span>],
											['by', s.submittedBy],
											[
												'responses',
												s.responses?.length
													? `${s.responses.length} · last: ${last?.summary ?? last?.kind ?? '—'}`
													: 'none yet',
											],
											['errors', errs ? String(errs) : null, 'rd'],
											['warnings', warns ? String(warns) : null, 'a'],
											['last polled', when(s.lastPolledAt)],
										]}
									/>
								</tbody>
							</table>
						</div>
					);
				})}
			</div>
		</Section>
	);
};

export const CaseDrawer: FC<{
	/** The case to show; null closes the drawer. */
	kase: any | null;
	/** Every case for the subject, so the drawer can move between them. */
	cases: any[];
	onPick: (caseId: string) => void;
	onClose: () => void;
}> = ({ kase, cases, onPick, onClose }) => {
	const { draft, fincen, filer, loading } = useCaseSar(kase?._id ?? null, kase?.businessId ?? null);
	/* A case with no pinned evidence and no linked alert has nothing a SAR could
	   be built from. That is a fact about the CASE, so it is read from the case. */
	const draftEmpty =
		!(kase?.pinnedTransactionIds?.length ?? 0) && !(kase?.linkedAlertIds?.length ?? 0);
	if (!kase) return null;

	const [cv, cl] = CASE_STATUS[kase.status] ?? ['ns', String(kase.status)];
	const others = cases.filter(c => c._id !== kase._id);
	const prodAccepted = (fincen?.submissions ?? []).some(
		s => s.environment === 'production' && (s.status === 'ACCEPTED' || s.status === 'ACCEPTED_WITH_WARNINGS')
	);

	return (
		<Drawer open label="Case details">
			<DrawerHead
				title={kase.caseNumber}
				onClose={onClose}
				chip={<StatusChip verdict={cv}>{cl}</StatusChip>}
			>
				<div className="csar-title">{kase.title}</div>
				{others.length > 0 && (
					<div className="csar-others">
						<span>Other cases for this subject:</span>
						{others.map(c => (
							<button type="button" className="dbtn" key={c._id} onClick={() => onPick(c._id)}>
								{c.caseNumber} · {String(c.status).replace('_', ' ').toLowerCase()}
							</button>
						))}
					</div>
				)}
			</DrawerHead>

			<Section title="Case">
				<table className="kv wide">
					<tbody>
						<KVRows
							rows={[
								['id', <span className="mono" key="id">{kase._id}</span>],
								['assigned', kase.assignedTo ?? '—'],
								['alerts', String(kase.linkedAlertIds?.length ?? 0)],
								['outcome', kase.outcome ?? null],
								['opened', when(kase.createdAt)],
								[
									'marked filed',
									kase.sar?.filedAt ? (
										<span key="mf">
											{when(kase.sar.filedAt)}
											{!prodAccepted && (
												<span className="csar-warn">
													{' '}
													— a stamp in the panel, not a FinCEN transmission
												</span>
											)}
										</span>
									) : null,
								],
								['SAR reference', kase.sar?.reference ?? null],
							]}
						/>
					</tbody>
				</table>
			</Section>

			<ReadinessSection draft={draft} draftEmpty={draftEmpty} loading={loading} />
			<FilerSection filer={filer} draft={draft} loading={loading} />
			<SubmissionsSection fincen={fincen} loading={loading} />
		</Drawer>
	);
};
